import * as XLSX from 'xlsx';

export type QuizExcelQuestionType = 'MultipleChoice' | 'TrueFalse' | 'OneAnswer';

export interface QuizExcelAnswer {
  text: string;
  isCorrect: boolean;
}

export interface QuizExcelQuestion {
  text: string;
  type: QuizExcelQuestionType;
  imagePath: null;
  reason: string | null;
  answers: QuizExcelAnswer[];
}

export interface QuizExcelParseResult {
  questions: QuizExcelQuestion[];
  errors: string[];
}

const TEMPLATE_HEADERS = [
  'Question',
  'Type',
  'Option1',
  'Option2',
  'Option3',
  'Option4',
  'Option5',
  'Correct',
  'Reason'
] as const;

function cellToString(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}

function normalizeHeader(header: string): string {
  return header.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

function mapHeader(header: string): string | null {
  const h = normalizeHeader(header);
  const aliases: Record<string, string> = {
    question: 'question',
    text: 'question',
    'question text': 'question',
    type: 'type',
    'question type': 'type',
    option1: 'option1',
    'option 1': 'option1',
    a: 'option1',
    'option a': 'option1',
    option2: 'option2',
    'option 2': 'option2',
    b: 'option2',
    'option b': 'option2',
    option3: 'option3',
    'option 3': 'option3',
    c: 'option3',
    'option c': 'option3',
    option4: 'option4',
    'option 4': 'option4',
    d: 'option4',
    'option d': 'option4',
    option5: 'option5',
    'option 5': 'option5',
    e: 'option5',
    'option e': 'option5',
    correct: 'correct',
    'correct answer': 'correct',
    answer: 'correct',
    oneanswer: 'correct',
    'one answer': 'correct',
    reason: 'reason',
    explanation: 'reason'
  };
  return aliases[h] || null;
}

function normalizeType(raw: string): QuizExcelQuestionType | null {
  const t = raw.trim().toLowerCase().replace(/[_/-]+/g, ' ');
  if (!t || t === 'multiplechoice' || t === 'multiple choice' || t === 'mcq' || t === 'mc' || t === 'multiple') {
    return 'MultipleChoice';
  }
  if (t === 'truefalse' || t === 'true false' || t === 'tf' || t === 'true or false') {
    return 'TrueFalse';
  }
  if (t === 'oneanswer' || t === 'one answer' || t === 'short' || t === 'short answer' || t === 'open') {
    return 'OneAnswer';
  }
  return null;
}

function letterToIndex(value: string): number | null {
  const v = value.trim().toUpperCase();
  if (/^[A-E]$/.test(v)) return v.charCodeAt(0) - 65;
  if (/^[1-5]$/.test(v)) return parseInt(v, 10) - 1;
  return null;
}

function parseRow(row: Record<string, string>, rowNumber: number): { question?: QuizExcelQuestion; error?: string } {
  const questionText = row.question || '';
  if (!questionText) {
    return {};
  }

  const type = normalizeType(row.type || 'MultipleChoice');
  if (!type) {
    return { error: `Row ${rowNumber}: unknown type "${row.type}". Use MultipleChoice, TrueFalse, or OneAnswer.` };
  }

  const reason = row.reason ? row.reason : null;
  const options = [row.option1, row.option2, row.option3, row.option4, row.option5]
    .map(cellToString)
    .filter(Boolean);
  const correctRaw = row.correct || '';

  if (type === 'OneAnswer') {
    const answerText = correctRaw || options[0];
    if (!answerText) {
      return { error: `Row ${rowNumber}: OneAnswer questions need the answer in the Correct column.` };
    }
    return {
      question: {
        text: questionText,
        type,
        imagePath: null,
        reason,
        answers: [{ text: answerText, isCorrect: true }]
      }
    };
  }

  if (type === 'TrueFalse') {
    const tfOptions = options.length >= 2 ? options.slice(0, 2) : ['True', 'False'];
    let correctIndex = letterToIndex(correctRaw);
    if (correctIndex == null && correctRaw) {
      const lowered = correctRaw.toLowerCase();
      if (['true', 'yes', 't'].includes(lowered)) {
        correctIndex = tfOptions.findIndex(o => o.toLowerCase().startsWith('t'));
        if (correctIndex < 0) correctIndex = 0;
      } else if (['false', 'no', 'f'].includes(lowered)) {
        correctIndex = tfOptions.findIndex(o => o.toLowerCase().startsWith('f'));
        if (correctIndex < 0) correctIndex = 1;
      } else {
        correctIndex = tfOptions.findIndex(o => o.toLowerCase() === lowered);
      }
    }
    if (correctIndex == null || correctIndex < 0 || correctIndex > 1) {
      return { error: `Row ${rowNumber}: TrueFalse Correct must be True, False, 1, or 2.` };
    }
    return {
      question: {
        text: questionText,
        type,
        imagePath: null,
        reason,
        answers: tfOptions.map((text, index) => ({
          text,
          isCorrect: index === correctIndex
        }))
      }
    };
  }

  if (options.length < 2) {
    return { error: `Row ${rowNumber}: MultipleChoice needs at least 2 options.` };
  }

  let correctIndex = letterToIndex(correctRaw);
  if (correctIndex == null && correctRaw) {
    correctIndex = options.findIndex(o => o.toLowerCase() === correctRaw.toLowerCase());
  }
  if (correctIndex == null || correctIndex < 0 || correctIndex >= options.length) {
    return { error: `Row ${rowNumber}: Correct must be A-E, 1-5, or match an option text.` };
  }

  return {
    question: {
      text: questionText,
      type,
      imagePath: null,
      reason,
      answers: options.map((text, index) => ({
        text,
        isCorrect: index === correctIndex
      }))
    }
  };
}

export function parseQuizExcel(buffer: ArrayBuffer): QuizExcelParseResult {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames.find(name => /question/i.test(name)) || workbook.SheetNames[0];
  if (!sheetName) {
    return { questions: [], errors: ['The Excel file has no sheets.'] };
  }

  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' });
  if (rows.length < 2) {
    return { questions: [], errors: ['The Questions sheet must include a header row and at least one question.'] };
  }

  const headerRow = (rows[0] || []).map(cellToString);
  const columnIndex: Record<string, number> = {};
  headerRow.forEach((header, index) => {
    const mapped = mapHeader(header);
    if (mapped && columnIndex[mapped] == null) {
      columnIndex[mapped] = index;
    }
  });

  if (columnIndex.question == null) {
    return { questions: [], errors: ['Missing a Question column. Download the template and keep the header row.'] };
  }

  const questions: QuizExcelQuestion[] = [];
  const errors: string[] = [];

  for (let i = 1; i < rows.length; i += 1) {
    const raw = rows[i] || [];
    const isEmpty = raw.every(cell => !cellToString(cell));
    if (isEmpty) continue;

    const row: Record<string, string> = {};
    Object.entries(columnIndex).forEach(([key, index]) => {
      row[key] = cellToString(raw[index]);
    });

    const parsed = parseRow(row, i + 1);
    if (parsed.error) errors.push(parsed.error);
    if (parsed.question) questions.push(parsed.question);
  }

  if (questions.length === 0 && errors.length === 0) {
    errors.push('No questions were found in the file.');
  }

  return { questions, errors };
}

export function downloadQuizExcelTemplate() {
  const instructions = [
    ['Quiz Excel import template'],
    [''],
    ['How to use'],
    ['1. Open the Questions sheet.'],
    ['2. Keep the header row. Add one question per row.'],
    ['3. Type must be MultipleChoice, TrueFalse, or OneAnswer.'],
    ['4. For MultipleChoice, fill Option1-Option4 (Option5 is optional).'],
    ['5. Correct: A/B/C/D/E or 1/2/3/4/5, or the option text. For TrueFalse use True or False. For OneAnswer put the answer in Correct.'],
    ['6. Reason is optional.'],
    ['7. Save the file and upload it in the admin import dialog.']
  ];

  const questions = [
    [...TEMPLATE_HEADERS],
    [
      'What is 2 + 2?',
      'MultipleChoice',
      '3',
      '4',
      '5',
      '6',
      '',
      'B',
      '2 + 2 equals 4'
    ],
    [
      'The earth is round.',
      'TrueFalse',
      'True',
      'False',
      '',
      '',
      '',
      'True',
      ''
    ],
    [
      'What is the capital of France?',
      'OneAnswer',
      '',
      '',
      '',
      '',
      '',
      'Paris',
      ''
    ]
  ];

  const workbook = XLSX.utils.book_new();
  const instructionsSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionsSheet['!cols'] = [{ wch: 90 }];
  const questionsSheet = XLSX.utils.aoa_to_sheet(questions);
  questionsSheet['!cols'] = TEMPLATE_HEADERS.map(header => ({
    wch: Math.max(14, header.length + 4)
  }));

  XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');
  XLSX.utils.book_append_sheet(workbook, questionsSheet, 'Questions');
  XLSX.writeFile(workbook, 'quiz-import-template.xlsx');
}

export async function readQuizExcelFile(file: File): Promise<QuizExcelParseResult> {
  const buffer = await file.arrayBuffer();
  return parseQuizExcel(buffer);
}
